import animation, { shimmer } from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { dsStylesOverride } from '@helpers/global';
import { Device, from } from '@helpers/media';
import { LoaderStyles } from '@stories/Elements/Misc/Loader/Loader.styles';
import styled, { css, keyframes } from 'styled-components';

const LocationIcon = keyframes`
  0% { 
    transform: scale(1);
  }	
  50% {
    transform: scale(1.4);
  }
  100% { 
    transform: scale(1);
  }	
`;

export const ButtonIcon = styled.i`
  position: absolute;
  top: 50%;
  right: 14px;
  width: 14px;
  height: 14px;
  opacity: 0;
  transform: translateY(-50%) translateX(-14px);
  transition: transform 0.3s ${animation.easeInOutCubic}, opacity 0.3s ${animation.easeInOutCubic};
  z-index: 2;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

interface baseButtonStylesProps {
  alignCentre?: boolean;
  theme:
    | 'red'
    | 'red-white'
    | 'white'
    | 'black'
    | 'black-grey'
    | 'grey'
    | 'grey-outline'
    | 'dark-grey'
    | 'green'
    | 'black-outline'
    | 'white-red'
    | 'white-outline';
  size: 'small' | 'normal';
  textTheme: 'black' | undefined;
  underline: boolean;
  iconPadding: boolean;
  maintainWidth?: boolean;
}

const baseButtonStyles = css<baseButtonStylesProps>`
  outline: none;
  border: none;
  line-height: 1.2;
  ${fonts.helvetica['83-heavy-extended']};
  letter-spacing: ${fonts.ls(15)};
  text-transform: uppercase !important;
  padding: 14px;
  display: inline-block;
  border-radius: 3px;
  text-decoration: ${({ underline }) => (underline ? 'underline' : '')};
  min-width: 90px;
  width: auto;
  text-align: center;
  height: auto;
  transition: background 375ms ${animation.easeInOutCubic};
  position: relative;
  user-select: none;
  cursor: pointer;

  &[disabled] {
    opacity: 0.5;
  }

  ${({ size, maintainWidth }) => {
    return css`
      &[data-icon-visibility='always'] {
        ${() => {
          switch (size) {
            case 'normal': {
              return css`
                padding: 14px 40px 14px 14px;
              `;
            }

            case 'small': {
              return css`
                padding: 10px 40px 10px 10px;
              `;
            }
          }
        }}

        ${ButtonIcon} {
          opacity: 1;
          transform: translateY(-50%) translateX(0px);
        }
      }

      &[data-icon-visibility='onHover'] {
        ${ButtonIcon} {
          transition: none;
        }

        &:hover {
          padding-right: 40px;

          ${ButtonIcon} {
            position: absolute;
            opacity: 1;
            transform: translateY(-50%) translateX(0px);
          }
        }

        ${maintainWidth &&
        css`
          min-width: unset;

          &:not(:hover) {
            margin-right: 26px;
          }
        `}
      }
    `;
  }}

  ${({ size }) => {
    switch (size) {
      default:
      case 'small':
        return css`
          font-size: 12px;
        `;

      case 'normal':
        return css`
          font-size: 14px;

          @media ${from(Device.MobileLarge)} {
            font-size: 16px;
          }
        `;
    }
  }}

  &[data-icon="arrow"] {
    &[href*='#'] {
      ${ButtonIcon} {
        transform: translateY(-50%) translateX(-14px) rotate(90deg);
      }

      &:hover {
        ${ButtonIcon} {
          transform: translateY(-50%) translateX(0px) rotate(90deg);
        }
      }
    }
  }

  &[data-icon='location'] {
    display: flex;
    align-items: center;

    svg {
      width: 10px;
      height: 14px;
      fill: ${brand.white};
    }

    &:hover {
      color: ${brand.white};
      svg {
        animation: ${LocationIcon} 2s linear infinite;
        animation-play-state: running;
      }
    }

    &[data-icon-placement='after'] {
      svg {
        margin-left: 8px;
      }
    }

    &[data-icon-placement='before'] {
      svg {
        margin-right: 8px;
      }
    }
  }

  &[data-icon='download'] {
    display: flex;
    align-items: center;

    svg {
      transform: translateY(1px);
    }

    &[data-icon-placement='after'] {
      svg {
        margin-left: 8px;
      }

      ${ButtonIcon} {
        right: 20px;
        left: unset;
      }
    }

    &[data-icon-placement='before'] {
      margin-right: 8px;
    }
  }

  &[data-icon='loader'] {
    ${ButtonIcon} {
      width: 18px;
      height: 18px;
    }

    ${LoaderStyles.Loader} {
      width: 18px;
      height: 18px;
      border-width: 2px;
      margin: 0px;
    }
  }

  &[data-icon='upload'] {
    ${ButtonIcon} {
      width: 18px;
      height: 18px;
    }

    &[data-icon-placement='after'] {
      padding: 14px 44px 14px 14px;
    }

    &[data-icon-placement='before'] {
      padding: 14px 14px 14px 44px;

      ${ButtonIcon} {
        right: unset;
        left: 14px;
      }
    }
  }

  &[data-icon='view-in-ar'] {
    display: inline-flex;
    align-items: center;
    padding-right: 16px;

    svg {
      height: 28px;
      width: 25px;
      margin-right: 10px;

      @media ${from(Device.TabletLarge)} {
        height: 32px;
        width: 28px;
      }
    }
  }

  &[data-icon='gallery'] {
    ${ButtonIcon} {
      width: 18px;
      height: 18px;
    }

    &[data-icon-placement='after'] {
      padding: 14px 44px 14px 14px;
    }

    &[data-icon-placement='before'] {
      padding: 14px 14px 14px 44px;

      ${ButtonIcon} {
        right: unset;
        left: 14px;
      }
    }
  }

  &[data-icon='onekey'] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-right: 16px;
    grid-gap: 10px;

    ${ButtonIcon} {
      position: relative;
      width: 120px;
      height: 32px;
      transform: none;
      right: 0;
    }

    svg {
      width: 120px;
      height: 32px;
    }
  }

  ${({ theme, textTheme }) => {
    switch (theme) {
      case 'red': {
        return css`
          background-color: ${brand.red.base};

          &:not([disabled]):hover {
            background-color: ${brand.red.dark};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${dsStylesOverride(` color: ${brand.white};`)};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'red-white': {
        return css`
          background-color: ${brand.red.base};
          border: 1px solid;

          &:not([disabled]):hover {
            background-color: ${brand.white};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.red.base};
                  }

                  &:hover {
                    color: ${brand.red.base};
                  }
                `;
            }
          }}
        `;
      }

      case 'black-outline': {
        return css`
          outline: 1px solid ${brand.black};
          background-color: transparent;

          &:not([disabled]):hover {
            background-color: transparent;
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.black};

                  ${dsStylesOverride(` color: ${brand.black};`)};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
            }
          }}
        `;
      }

      case 'grey-outline': {
        return css`
          outline: 1px solid ${brand.grey[45]};
          background-color: transparent;

          &:not([disabled]):hover {
            background-color: transparent;
            color: ${brand.grey[60]};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.grey[45]};

                  ${ButtonIcon} svg {
                    fill: ${brand.grey[45]};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.grey[45]};

                  ${dsStylesOverride(` color: ${brand.grey[45]};;`)};

                  ${ButtonIcon} svg {
                    fill: ${brand.grey[45]};
                  }
                `;
            }
          }}
        `;
      }

      case 'white': {
        return css`
          background-color: ${brand.white};

          &:not([disabled]):hover {
            background-color: ${brand.grey[90]};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.red.base};

                  ${ButtonIcon} svg {
                    fill: ${brand.red.base};
                  }
                `;
            }
          }}
        `;
      }

      case 'black': {
        return css`
          background-color: ${brand.black};

          &:not([disabled]):hover {
            background-color: ${brand.grey[20]};
            color: ${brand.white};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'shimmer': {
        return css`
          ${shimmer};
          color: ${brand.grey[70]};
        `;
      }

      case 'black-grey': {
        return css`
          background-color: ${brand.black};

          &:not([disabled]):hover {
            background-color: ${brand.grey[20]};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'grey': {
        return css`
          background-color: ${brand.grey[45]};

          &:not([disabled]):hover {
            background-color: ${brand.grey[40]};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'dark-grey': {
        return css`
          background-color: ${brand.grey[20]};

          &:not([disabled]):hover {
            background-color: ${brand.grey[15]};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'green': {
        return css`
          background-color: ${brand.green.base};

          &:not([disabled]):hover {
            background-color: ${brand.green.dark};
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.white};

                  background-color: ${brand.green.darker};

                  &:not([disabled]):hover {
                    background-color: ${brand.green.darkest};
                  }

                  ${ButtonIcon} svg {
                    fill: ${brand.white};
                  }
                `;
            }
          }}
        `;
      }

      case 'white-red': {
        return css`
          background-color: ${brand.white};
          border: 1px solid ${brand.red.base};

          &:not([disabled]):hover {
            background-color: ${brand.red.base};
            border: 1px solid ${brand.red.base};
            color: ${brand.white};

            ${ButtonIcon} svg {
              fill: ${brand.white};
            }
          }

          ${() => {
            switch (textTheme) {
              case 'black': {
                return css`
                  color: ${brand.black};

                  ${ButtonIcon} svg {
                    fill: ${brand.black};
                  }
                `;
              }
              default:
                return css`
                  color: ${brand.red.base};

                  ${ButtonIcon} svg {
                    fill: ${brand.red.base};
                  }
                `;
            }
          }}
        `;
      }

      case 'transparent': {
        return css`
          background-color: none;
          border: 1px solid ${brand.white};
          color: ${brand.white};

          &:not([disabled]):hover {
            background-color: ${brand.white};
            color: ${brand.red.base};
          }
        `;
      }

      case 'white-outline': {
        return css`
          background-color: ${brand.black};
          border: 1px solid ${brand.white};
          color: ${brand.white};

          &:not([disabled]):hover {
            background-color: ${brand.white};
            color: ${brand.black};
          }
        `;
      }
    }
  }};
`;

const LinkElement = styled.a<baseButtonStylesProps & { disabled: boolean }>`
  ${baseButtonStyles};

  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};
`;

const ButtonElement = styled.button.attrs({ type: 'button' })<baseButtonStylesProps>`
  ${baseButtonStyles};
  justify-content: ${(props) => (props.alignCentre ? 'center' : 'flex-start')};
  display: inline-block;
`;

const DownloadIconHolder = styled.div`
  transform: scale(0.8);
`;

const Arrow = styled.div`
  transition: transform 0.5s ${animation.easeInOutCubic};
`;

export const ButtonStyles = {
  ButtonElement,
  LinkElement,
  DownloadIconHolder,
  Arrow,
};
